"use client";
// ===================================================
// GAM Command Center — Filter Shell queries (multi-surface)
// Reads + writes the vdev_filter_columns config table (spec: APP-filter-shell).
// Generalized 2026-07-08: one config table + one FilterShell UI serve multiple
// surfaces (vdev, task_inbox, ...) via the `surface` column. Pattern mirrors
// pm-queries.ts / pm-mutations.ts.
// ===================================================

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";

export interface FilterColumn {
  id: string;
  tenant_id: string;
  surface: string;
  col_key: string;
  label: string;
  source_field: string;
  visible: boolean;
  sort_order: number;
  width: number;
}

const TENANT = "gam";
const BASE_KEY = ["vdev_filter_columns"];

/** Width-only writes preserve visibility/order owned by the column browser. */
export function useSaveColumnWidths(surface: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['column-widths', surface],
    scope: { id: `column-widths:${surface}` },
    retry: 2,
    mutationFn: async (columns: Pick<FilterColumn, "col_key" | "label" | "source_field" | "width">[]) => {
      const { data, error } = await supabase.from("vdev_filter_columns")
        .upsert(columns.map(column => ({ ...column, tenant_id: TENANT, surface })),
          { onConflict: "tenant_id,surface,col_key" })
        .select("*");
      if (error) throw error;
      return data as FilterColumn[];
    },
    onSuccess: rows => qc.setQueryData<FilterColumn[]>([...BASE_KEY, surface], previous => [
      ...(previous ?? []).filter(column => !rows.some(row => row.col_key === column.col_key)),
      ...rows,
    ]),
  });
}

// `enabled` lets a consumer mount the hook unconditionally (React rules) and
// only fire the query when it actually has a surface — GamTable without a
// `surface` prop must not hit the DB at all.
export function useFilterColumns(surface: string = "vdev", opts?: { enabled?: boolean }) {
  return useQuery<FilterColumn[]>({
    queryKey: [...BASE_KEY, surface],
    enabled: opts?.enabled ?? true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vdev_filter_columns")
        .select("*")
        .eq("tenant_id", TENANT)
        .eq("surface", surface)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as FilterColumn[];
    },
    staleTime: 60_000,
  });
}

// Update one column (visible / width). Optimistic invalidation on success.
export function useUpdateFilterColumn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<Pick<FilterColumn, "visible" | "width">>;
    }) => {
      const { error } = await supabase
        .from("vdev_filter_columns")
        .update(patch)
        .eq("id", id);
      if (error) throw error;
    },
    // Base key (no surface segment) invalidates every surface's query — cheap, correct.
    onSuccess: () => qc.invalidateQueries({ queryKey: BASE_KEY }),
  });
}

// Upsert one column's visibility by (tenant, surface, col_key) — the table's
// UNIQUE key. Used by GamTable's "עמודות" menu: a surface that has never been
// seen gets its rows created on the first toggle, an existing row is patched
// in place. Only the provided fields are written, so width/sort_order set by
// FilterShell's manage panel survive a visibility toggle from the table.
export function useUpsertFilterColumn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      surface,
      col_key,
      label,
      source_field,
      visible,
    }: Pick<FilterColumn, "surface" | "col_key" | "label" | "source_field" | "visible">) => {
      const { error } = await supabase
        .from("vdev_filter_columns")
        .upsert(
          { tenant_id: TENANT, surface, col_key, label, source_field, visible },
          { onConflict: "tenant_id,surface,col_key" },
        );
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BASE_KEY }),
  });
}

export type FacetSelection = Record<string, string[]>; // col_key → selected values

// Resolve a row's value for a facet column (shared by the shell + the page filter).
// Generic over any row shape — a nameMap entry for a field (e.g. app_notion_id,
// app_id) means "resolve this raw id through the map", regardless of naming.
export function facetValue<T extends object>(
  row: T,
  col: FilterColumn,
  maps: Record<string, Map<string, string>>,
): string {
  const raw = (row as Record<string, unknown>)[col.source_field];
  if (maps[col.source_field]) {
    if (!raw) return "—";
    return maps[col.source_field]?.get(String(raw)) ?? String(raw);
  }
  if (col.source_field === "due_date") {
    return raw ? new Date(String(raw)).toLocaleDateString("he-IL") : "(ללא תאריך)";
  }
  return raw ? String(raw) : "—";
}

// AND across columns, OR within a column.
export function rowMatchesSelection<T extends object>(
  row: T,
  columns: FilterColumn[],
  maps: Record<string, Map<string, string>>,
  selection: FacetSelection,
): boolean {
  for (const col of columns) {
    const selected = selection[col.col_key];
    if (!selected || selected.length === 0) continue;
    if (!selected.includes(facetValue(row, col, maps))) return false;
  }
  return true;
}

// Back-compat alias — vdev call sites historically imported `taskMatchesSelection`.
export const taskMatchesSelection = rowMatchesSelection;

// Persist a new column order: assign sort_order = index+1 to each id in turn.
export function useReorderFilterColumns() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await Promise.all(
        orderedIds.map((id, i) =>
          supabase
            .from("vdev_filter_columns")
            .update({ sort_order: i + 1 })
            .eq("id", id),
        ),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: BASE_KEY }),
  });
}
